import React, { useState } from 'react';
import { Button } from './Button';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface ContactFormErrors {
  name?: string;
  email?: string;
  phone?: string;
  subject?: string;
  message?: string;
}

interface ContactFormProps {
  onSubmit?: (data: ContactFormData) => Promise<void>;
  className?: string;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  onSubmit,
  className = ''
}) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateForm = (): boolean => {
    const newErrors: ContactFormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation (optional but if provided, should be valid)
    if (formData.phone.trim()) {
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      const cleanPhone = formData.phone.replace(/[\s\-\(\)]/g, '');
      if (!phoneRegex.test(cleanPhone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof ContactFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      if (onSubmit) {
        await onSubmit(formData);
      } else {
        // Default behavior - simulate form submission
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-6 ${className}`} noValidate>
      {/* Form Header */}
      <div className="text-center mb-8">
        <h3 className="heading-md text-primary-navy mb-4">
          Send Us a Message
        </h3>
        <p className="body-base text-text-medium">
          Fill out the form below and we'll get back to you as soon as possible.
        </p>
      </div>

      {/* Success Message */}
      {submitStatus === 'success' && (
        <div
          className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6"
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-green-600 mr-3"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className="body-sm text-green-800 font-medium">
              Thank you for your message. We'll be in touch soon.
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {submitStatus === 'error' && (
        <div
          className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-red-600 mr-3"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="body-sm text-red-800 font-medium">
              There was an error sending your message. Please try again.
            </p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Name Field */}
        <div>
          <label
            htmlFor="contact-name"
            className="block body-sm font-medium text-primary-navy mb-2"
          >
            Full Name <span className="text-red-500" aria-label="required">*</span>
          </label>
          <input
            type="text"
            id="contact-name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-lg body-base transition-colors focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent placeholder:text-gray-400 ${errors.name
                ? 'border-red-300 bg-red-50'
                : 'border-border-subtle bg-white hover:border-primary-navy/30'
              }`}
            placeholder="Enter your full name"
            aria-describedby={errors.name ? 'name-error' : undefined}
            aria-invalid={errors.name ? 'true' : 'false'}
            required
          />
          {errors.name && (
            <p
              id="name-error"
              className="mt-2 body-sm text-red-600"
              role="alert"
            >
              {errors.name}
            </p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label
            htmlFor="contact-email"
            className="block body-sm font-medium text-primary-navy mb-2"
          >
            Email Address <span className="text-red-500" aria-label="required">*</span>
          </label>
          <input
            type="email"
            id="contact-email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-lg body-base transition-colors focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent placeholder:text-gray-400 ${errors.email
                ? 'border-red-300 bg-red-50'
                : 'border-border-subtle bg-white hover:border-primary-navy/30'
              }`}
            placeholder="Enter your email address"
            aria-describedby={errors.email ? 'email-error' : undefined}
            aria-invalid={errors.email ? 'true' : 'false'}
            required
          />
          {errors.email && (
            <p
              id="email-error"
              className="mt-2 body-sm text-red-600"
              role="alert"
            >
              {errors.email}
            </p>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Phone Field */}
        <div>
          <label
            htmlFor="contact-phone"
            className="block body-sm font-medium text-primary-navy mb-2"
          >
            Phone Number <span className="body-xs text-text-light">(Optional)</span>
          </label>
          <input
            type="tel"
            id="contact-phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-lg body-base transition-colors focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent placeholder:text-gray-400 ${errors.phone
                ? 'border-red-300 bg-red-50'
                : 'border-border-subtle bg-white hover:border-primary-navy/30'
              }`}
            placeholder="+91 98765 43210"
            aria-describedby={errors.phone ? 'phone-error' : undefined}
            aria-invalid={errors.phone ? 'true' : 'false'}
          />
          {errors.phone && (
            <p
              id="phone-error"
              className="mt-2 body-sm text-red-600"
              role="alert"
            >
              {errors.phone}
            </p>
          )}
        </div>

        {/* Subject Field */}
        <div>
          <label
            htmlFor="contact-subject"
            className="block body-sm font-medium text-primary-navy mb-2"
          >
            Subject <span className="text-red-500" aria-label="required">*</span>
          </label>
          <select
            id="contact-subject"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-lg body-base transition-colors focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent ${errors.subject
                ? 'border-red-300 bg-red-50'
                : 'border-border-subtle bg-white hover:border-primary-navy/30'
              }`}
            aria-describedby={errors.subject ? 'subject-error' : undefined}
            aria-invalid={errors.subject ? 'true' : 'false'}
            required
          >
            <option value="">Select a subject</option>
            <option value="General Inquiry">General Inquiry</option>
            <option value="Legal Consultation">Legal Consultation</option>
            <option value="Corporate Law">Corporate Law</option>
            <option value="Litigation">Litigation</option>
            <option value="Intellectual Property">Intellectual Property</option>
            <option value="Real Estate">Real Estate</option>
            <option value="Employment Law">Employment Law</option>
            <option value="Other">Other</option>
          </select>
          {errors.subject && (
            <p
              id="subject-error"
              className="mt-2 body-sm text-red-600"
              role="alert"
            >
              {errors.subject}
            </p>
          )}
        </div>
      </div>

      {/* Message Field */}
      <div>
        <label
          htmlFor="contact-message"
          className="block body-sm font-medium text-primary-navy mb-2"
        >
          Message <span className="text-red-500" aria-label="required">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={6}
          value={formData.message}
          onChange={handleInputChange}
          className={`w-full px-4 py-3 border rounded-lg body-base transition-colors focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent resize-vertical placeholder:text-gray-400 ${errors.message
              ? 'border-red-300 bg-red-50'
              : 'border-border-subtle bg-white hover:border-primary-navy/30'
            }`}
          placeholder="Please describe your legal inquiry or how we can assist you..."
          aria-describedby={errors.message ? 'message-error' : undefined}
          aria-invalid={errors.message ? 'true' : 'false'}
          required
        />
        {errors.message && (
          <p
            id="message-error"
            className="mt-2 body-sm text-red-600"
            role="alert"
          >
            {errors.message}
          </p>
        )}
      </div>

      {/* Legal Disclaimer */}
      <div className="bg-accent-gold/5 border border-accent-gold/20 rounded-lg p-4">
        <h4 className="body-sm font-semibold text-primary-navy mb-2">
          Important Legal Notice
        </h4>
        <p className="body-xs text-text-medium leading-relaxed">
          By submitting this form, you acknowledge that: (1) This communication does not create an attorney-client relationship;
          (2) You should not include confidential or sensitive information; (3) Submission of this form does not guarantee
          that VC Law Associates will represent you or provide legal services; (4) Any information you provide may not be
          protected by attorney-client privilege until a formal engagement is established.
        </p>
      </div>

      {/* Submit Button */}
      <div className="text-center">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={isSubmitting}
          className="min-w-[200px]"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Sending...
            </span>
          ) : (
            'Send Message'
          )}
        </Button>
      </div>
    </form>
  );
};